<template>
   <div data-cy="personalised-trait-report-card" class="mx-auto my-10 w-full max-w-md md:max-w-xl">
      <!-- [theme class css in cms-themes.scss] -->
      <div class="rounded-3xl px-8 py-12 md:mx-6 md:py-16 lg:px-16" :class="`theme-${props.reportCard?.color}`">
         <div class="relative w-full items-center justify-between border-b pb-8 md:flex md:pb-12">
            <!-- Header -->
            <div class="flex items-center">
               <div class="mr-4 w-8 opacity-70">
                  <IconSano />
               </div>
               <h2 class="text-bolder text-2xl leading-tight">
                  {{ $t("dna-testing.trait_reports.report_card_title") }}
               </h2>
            </div>
            <!-- Heritability & explanation popup -->
            <div v-if="!!heritabilityLevel" class="mt-6 flex justify-end md:mt-0">
               <Popup :buttonAria="heritabilityPopupAria">
                  <template v-slot:button-content>
                     <div
                        data-cy="heritability-btn"
                        class="text-bolder mr-1 flex rounded-full border border-current text-sm leading-tight"
                        aria-hidden="true"
                     >
                        <div
                           class="flex items-center rounded-l-full py-1 pl-4 pr-3"
                           style="background-color: currentcolor"
                        >
                           <div class="pr-2">
                              <IconArrowShortLine
                                 v-if="heritabilityLevel === 'High' || heritabilityLevel === 'Low'"
                                 class="h-2 w-3"
                                 :style="`transform: rotate(${heritabilityLevel === 'Low' ? 90 : 270}deg)`"
                              />

                              <div
                                 v-else-if="heritabilityLevel === 'Mid'"
                                 class="flex h-1.5 w-2 items-center border-y border-white"
                              ></div>
                           </div>
                           <div class="capitalize text-white">
                              <span>{{ heritabilityLevel }}</span>
                              <span v-if="heritabilityNumbers"> ({{ heritabilityNumbers }})</span>
                           </div>
                        </div>
                        <div class="py-1 pl-3 pr-4">{{ $t("dna-testing.trait_reports.heritability") }}</div>
                     </div>
                     <span class="text-bolder flex size-6 items-center justify-center rounded-full text-sm">
                        <span
                           style="background-color: currentcolor"
                           class="absolute left-0 top-0 size-full rounded-full opacity-30"
                        ></span>
                        <span class="relative" aria-hidden="true">?</span>
                     </span>
                  </template>
                  <template v-slot:popup-content>
                     <div data-cy="heritability-info" class="rounded-lg bg-white px-6 py-4 text-sm leading-snug">
                        <p class="text-bolder mb-2">{{ $t("dna-testing.trait_reports.heritability_means") }}</p>
                        <p class="mb-2">{{ $t("dna-testing.trait_reports.example_eye_colour") }}</p>
                        <p>{{ $t("dna-testing.trait_reports.heritability_doesnt_mean") }}</p>
                     </div>
                  </template>
               </Popup>
            </div>
         </div>
         <!-- Report card content -->
         <div class="my-2 flex flex-wrap">
            <div
               v-for="(row, i) in rows"
               :key="`${props.reportKey}-info-card-${i}`"
               :class="`w-full flex-shrink-0 ${row.largeScreenPosition}`"
            >
               <RichTextRenderer v-if="row.type === 'richTextRenderer'" :document="row.content"></RichTextRenderer>
               <SnpChart
                  v-if="row.type === 'snpChart'"
                  :userVariants="props.userVariants"
                  :content="row.content"
                  :reportSnpInfo="reportSnpInfo"
                  :color="reportCard.color"
               ></SnpChart>
               <CombinedSnpsEffectChart
                  v-if="row.type === 'combinedSnpsEffectChart'"
                  :userVariants="props.userVariants"
                  :content="row.content"
                  :color="reportCard.color"
               >
               </CombinedSnpsEffectChart>
               <div v-if="row.type === 'customHtml'" v-html="row.content"></div>
            </div>
         </div>

         <div v-if="isConditionReport" class="mt-12 flex shrink-0 md:mt-16">
            <div
               class="mr-1 mt-1 flex size-4 shrink-0 items-center justify-center rounded-full bg-black md:mr-3"
               :class="`bg-${reportCard.color}-90`"
            >
               <IconExclamationCircle aria-hidden="true" :class="`w-3.5 h-3.5 fill-${reportCard.color}-10`" />
            </div>

            <p class="text-sm">
               <span class="text-bolder">{{ $t("dna-testing.trait_reports.please_note") }}</span>
               <span v-if="isConditionReport" data-cy="not-medical-diagnosis-note" class="text-bolder"
                  >{{ $t("dna-testing.trait_reports.not_diagnosis_note") }}&nbsp;</span
               >
               <!-- Re-add once client site is sunsetted and this information can be moved from content to card -->
               <!-- <span>{{ $t("dna-testing.trait_reports.disclaimer") }}</span> -->
            </p>
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { ContainerTheme } from "@/types";

const { t: $t } = useI18n();

// Interfaces
interface rowObj {
   type: string;
   content: any;
   largeScreenPosition: "flexLeft" | "flexRight" | "full";
}

interface reportCardObj {
   color: ContainerTheme;
   rows: rowObj[];
}

// Props
const props = defineProps<{
   reportCard: reportCardObj;
   userVariants: { [key: string /* RSID */]: string /* Variant e.g. AA */ };
   reportKey: string;
   heritability?: string;
   isConditionReport: boolean;
   reportSnpInfo: {
      [key: string /* RSID */]: {
         [key: string /* Variant e.g. TT */]: string /* Detailed description of variant effect */;
      };
   };
}>();

// Computed properties
const rows = computed(() => {
   return props.reportCard.rows;
});

const heritabilityLevel = computed(() => {
   if (props.heritability?.includes("low")) return "Low";
   if (props.heritability?.includes("mid")) return "Mid";
   if (props.heritability?.includes("high")) return "High";
   return null;
});

// Sometimes we will have more specific information about heritability, e.g. 20-50%. This will be separated from the more general label by a comma
const heritabilityNumbers = computed(() => {
   const splitInfo = props.heritability.split(",");
   if (splitInfo[1]) return splitInfo[1].trim();
   return null;
});

const heritabilityPopupAria = computed(() => {
   return `${$t("dna-testing.trait_reports.heritability")}: ${heritabilityLevel.value} ${
      heritabilityNumbers.value ? `- ${heritabilityNumbers.value}` : ""
   }. ${$t("system.popup.popup_toggle_show")}`;
});
</script>

<style scoped lang="scss">
.full {
   @apply pt-10;
}

.flexLeft {
   @apply pt-10;
}

.flexRight {
   @apply pt-4;
}

@media (min-width: 62rem) {
   .flexLeft {
      @apply w-2/5 pr-16;
   }

   .flexRight {
      @apply w-3/5 pt-10;
   }
}
</style>
